/** @format */

import { memo } from 'react'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import browser_jsImg from 'assets/images/browser-js.png'
import dom_treeImg from 'assets/images/dom-tree.png'
import dom_parseImg from 'assets/images/dom-parse.gif'
import js_engImg from 'assets/images/js-eng.gif'
import dom_tree_illustrationImg from 'assets/images/dom-tree-illustration.png'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Объектная модель документа</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Введение</h3>
      <p className={styles.description}>
        Когда мы работаем с браузером, доступный функционал состоит из нескольких частей, так как JavaScript сам по себе
        не имеет инструментов для работы с браузером.
      </p>
      <img className={styles.img} src={browser_jsImg} alt="browser-js" />
      <p className={styles.description}>
        <b>DOM (Document Object Model, Объектная модель документа)</b> — межплатформенный, независимый от языка
        интерфейс для работы с HTML-документом. Содержит набор свойств и методов позволяющих искать, создавать и удалять
        элементы, реагировать на действия пользователя и другое.{' '}
      </p>
      <p className={styles.description}>
        <b>BOM (Browser Object Model, Объектная модель браузера)</b> — межплатформенный, независимый от языка интерфейс
        для работы с окном браузера. Содержит набор свойств и методов позволяющих получить доступ непосредственно к
        текущей вкладке и ряду функций браузера. Включает объект работы с историей, местоположением и другое.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Введение</h3>
      <p className={styles.description}>
        DOM является отражением HTML-документа. Это древовидная структура, в которой каждый узел это JavaScript-объект
        со свойсвами и методами, представляющий часть HTML-документа. Объекты могут управляться программно, и любые
        изменения отражаются в документе.
      </p>
      <p className={styles.description}>
        DOM выполняет две роли: является объектным представлением HTML-документа, и действует как интерфейс, соединяющий
        страницу с языком программирования, например JavaScript.
      </p>
      <p className={styles.description}>
        Каждый элемент в документе, весь документ в целом, заголовок, ссылка, абзац — это части DOM этого документа,
        поэтому все они могут изменяться с помощью JavaScript.
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.3 - HTML-документ и DOM</h3>
      <p className={styles.description}>
        Согласно DOM-модели каждый тег образует отдельный <b>элемент-узел</b>, каждый фрагмент текста —{' '}
        <b>текстовый элемент</b>, таким образом HTML-документ это иерархическое дерево. Это означает, что у каждого
        элемента (кроме корневого) есть только один родитель, т.е. элемент, внутри которого он располагается. Это дерево
        образуется за счет вложенной структуры тегов и текстовых элементов.
      </p>
      <img className={styles.img} src={dom_treeImg} alt="dom-tree" />
      <p className={styles.description}>
        Чтобы отобразить HTML-документ, браузер сначала преобразовавает его в формат который он понимает — DOM. У движка
        браузера есть специальный фрагмент кода — <code className={styles.code}>HTML-парсер</code>, который используется
        для преобразования HTML в DOM.
      </p>
      <p className={styles.description}>
        В HTML, вложенность определяет отношения <b>родитель-ребенок</b> между элементами. В DOM, объекты связаны в
        древовидной структуре данных, фиксируя эти отношения.
      </p>
      <p className={styles.description}>
        Браузер строит DOM постепенно, как только приходят первые фрагменты кода, он начинает парсить HTML, добавляя
        узлы в древовидную структуру.
      </p>
      <img className={styles.img} src={dom_parseImg} alt="dom-parse" />
      <p className={styles.description}>
        После того как DOM-дерево построено, можно найти в нем элемент с помощью JavaScript и выполнять с ним какие-то
        действия, так как каждый узел имеет интерфейс с множеством свойств и методов.
      </p>
      <img className={styles.img} src={js_engImg} alt="js-eng" />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.4 - DOM-дерево</h3>
      <p className={styles.description}>
        Составим дерево HTML-документа используя{' '}
        <a
          className={styles.link}
          target="_blank"
          rel="noopener noreferrer"
          href="https://software.hixie.ch/utilities/js/live-dom-viewer/"
        >
          генератор DOM-дерева
        </a>
        .{' '}
      </p>
      <CopyBlock
        text={`<!DOCTYPE html>
<html>
  <head>
    <title>DOM</title>
  </head>
  <body>
    <div>
      <h2>Title</h2>
      <p>Some text</p>
      <ul>
        <li><a href="#">link 1</a></li>
        <li><a href="#">link 2</a></li>
        <li><a href="#">link 3</a></li>
      </ul>
    </div>
  </body>
</html>
`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
      <img className={styles.img} src={dom_tree_illustrationImg} alt="dom-tree-illustration" />
      <p className={styles.description}>В этом дереве выделены два типа узлов:</p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <b>Узлы-элементы (element node)</b> — образуются тегами, естественным образом одни узлы вложены в другие.
          Структура дерева образована исключительно за счет них.{' '}
        </li>
        <li className={styles.list__item}>
          <b>Текстовые узлы (text node)</b> — образуются текстом внутри элементов. Текстовый узел содержит только строку
          текста и не может иметь потомков, то есть он всегда на самом нижнем уровне иерархии. Пробелы и переносы строк
          это тоже текстовые узлы.{' '}
        </li>
      </ul>
      <p className={styles.notificationWarn}>
        Из этого правила есть исключения: пробелы до <code className={styles.code}>head</code> игнорируются, а любое
        содержимое после <code className={styles.code}>body</code> не создаёт узла, браузер переносит его в конец{' '}
        <code className={styles.code}>body</code>.
      </p>
    </section>
  </section>
))
